import { supabase } from "src/lib/supabaseClient";

export type NodeFeedbackLevel = "developing" | "on_track" | "secured";

export interface NodeFeedbackRequest {
  id: string;
  studentId: string;
  nodeType: "domain" | "unit" | "lesson";
  nodeId: string;
  nodeName: string;
  prompt: string;
  status: "open" | "submitted";
  progressLevel: NodeFeedbackLevel | null;
  detailedFeedback: string;
  pushedAt: string;
  submittedAt: string | null;
}

export interface NodeFeedbackSession {
  id: string;
  scheduled_at: string;
  tutor_id: string;
  tutor_name: string;
  objective_statement: string;
  feedback: Array<{
    id: string;
    submitted_by: string;
    user_role: string;
    rating: number | null;
    comments: string | null;
    strengths: string[] | null;
    areas_of_improvement: string[] | null;
  }>;
}

const nodeName = async (nodeType: string, nodeId: string) => {
  const table = nodeType === "domain" ? "curriculum_domains" : nodeType === "unit" ? "curriculum_units" : "curriculum_lessons";
  const { data, error } = await supabase.from(table).select("name").eq("id", nodeId).maybeSingle();
  if (error) throw new Error(error.message);
  return data?.name || "Curriculum node";
};

const mapRequest = async (row: { id: string; student_id: string; node_type: "domain" | "unit" | "lesson"; node_id: string; prompt_snapshot: string; status: "open" | "submitted"; progress_level: NodeFeedbackLevel | null; detailed_feedback: string | null; pushed_at: string; submitted_at: string | null }): Promise<NodeFeedbackRequest> => ({
  id: row.id,
  studentId: row.student_id,
  nodeType: row.node_type,
  nodeId: row.node_id,
  nodeName: await nodeName(row.node_type, row.node_id),
  prompt: row.prompt_snapshot,
  status: row.status,
  progressLevel: row.progress_level,
  detailedFeedback: row.detailed_feedback || "",
  pushedAt: row.pushed_at,
  submittedAt: row.submitted_at,
});

export const nodeFeedbackService = {
  async listForTutor(): Promise<NodeFeedbackRequest[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("Sign in again to view feedback requests.");
    const { data, error } = await supabase.from("node_feedback_requests").select("id, student_id, node_type, node_id, prompt_snapshot, status, progress_level, detailed_feedback, pushed_at, submitted_at").eq("assigned_tutor_id", user.id).order("pushed_at", { ascending: false });
    if (error) throw new Error(error.message);
    return Promise.all((data || []).map((row) => mapRequest(row)));
  },
  async listForStudent(): Promise<NodeFeedbackRequest[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("Sign in again to view curriculum progress.");
    const { data, error } = await supabase.from("node_feedback_requests").select("id, student_id, node_type, node_id, prompt_snapshot, status, progress_level, detailed_feedback, pushed_at, submitted_at").eq("student_id", user.id).order("pushed_at", { ascending: false });
    if (error) throw new Error(error.message);
    return Promise.all((data || []).map((row) => mapRequest(row)));
  },
  async listSessions(requestId: string): Promise<NodeFeedbackSession[]> {
    const { data, error } = await supabase.rpc("list_node_feedback_sessions", { p_request_id: requestId });
    if (error) throw new Error(error.message);
    return (data || []) as NodeFeedbackSession[];
  },
  async submit(requestId: string, progressLevel: NodeFeedbackLevel, detailedFeedback: string): Promise<void> {
    const feedback = detailedFeedback.trim();
    if (!feedback) throw new Error("Detailed feedback is required.");
    const { error } = await supabase.from("node_feedback_requests").update({ status: "submitted", progress_level: progressLevel, detailed_feedback: feedback }).eq("id", requestId);
    if (error) throw new Error(error.message);
  },
};
